/**
 * ═══════════════════════════════════════════════════════════════════════════
 * WASP Frontend v2.5.0 - Scheduler Configuration Tab
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @file SchedulerConfigurationTab.tsx
 * @description Tab de configuración del Scheduler
 * @version 1.0.0
 * @date 2025-11-28
 * 
 * SECCIONES:
 * 1. Zona Horaria - Configurar timezone por defecto
 * 2. Notificaciones - Alertas de ejecución/fallos
 * 3. Reintentos - Configurar reintentos automáticos
 * 4. Horario de Trabajo - Ventanas de envío permitidas
 * 5. Información del Sistema - Stats y datos del scheduler
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React, { useState } from 'react';
import { Card, Alert, Form, Row, Col, Button, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCog,
  faGlobe,
  faBell,
  faRepeat,
  faClock,
  faInfoCircle,
  faCheck,
  faSave
} from '@fortawesome/free-solid-svg-icons';
import { useTimeZone } from '../../../hooks/useTimeZone';
import { useScheduler } from '../../../hooks/useScheduler';
import { useRecurringCampaigns } from '../../../hooks/useRecurringCampaigns';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

interface SchedulerConfig {
  // Notifications
  notifyOnSuccess: boolean;
  notifyOnFailure: boolean;
  notificationEmail: string;
  notificationWhatsApp: string;
  
  // Retries
  enableRetries: boolean;
  maxRetries: number;
  retryDelayMinutes: number;
  
  // Working Hours
  enableWorkingHours: boolean;
  workingHoursStart: string;
  workingHoursEnd: string;
  workingDays: number[]; // 0=Sunday, 1=Monday, etc
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

const SchedulerConfigurationTab: React.FC = () => {
  // ═══════════════════════════════════════════════════════════════════════
  // HOOKS
  // ═══════════════════════════════════════════════════════════════════════
  
  const { currentTimezone, currentTimezoneInfo, setTimezone } = useTimeZone();
  const { schedules, stats } = useScheduler();
  const { activeCampaigns } = useRecurringCampaigns();

  // Lista de timezones comunes (hardcoded por ahora)
  const commonTimezones = [
    { value: 'America/Santiago', label: 'Chile (Santiago)' },
    { value: 'America/New_York', label: 'Estados Unidos (Nueva York)' },
    { value: 'America/Los_Angeles', label: 'Estados Unidos (Los Ángeles)' },
    { value: 'America/Mexico_City', label: 'México (Ciudad de México)' },
    { value: 'America/Buenos_Aires', label: 'Argentina (Buenos Aires)' },
    { value: 'America/Sao_Paulo', label: 'Brasil (São Paulo)' },
    { value: 'Europe/Madrid', label: 'España (Madrid)' },
    { value: 'Europe/London', label: 'Reino Unido (Londres)' },
    { value: 'Asia/Tokyo', label: 'Japón (Tokio)' },
    { value: 'UTC', label: 'UTC (Hora Universal)' }
  ];

  // ═══════════════════════════════════════════════════════════════════════
  // STATE
  // ═══════════════════════════════════════════════════════════════════════
  
  const [config, setConfig] = useState<SchedulerConfig>({
    // Notifications (default values)
    notifyOnSuccess: false,
    notifyOnFailure: true,
    notificationEmail: '',
    notificationWhatsApp: '',
    
    // Retries
    enableRetries: true,
    maxRetries: 3,
    retryDelayMinutes: 5,
    
    // Working Hours
    enableWorkingHours: false,
    workingHoursStart: '09:00',
    workingHoursEnd: '18:00',
    workingDays: [1, 2, 3, 4, 5] // Mon-Fri
  });

  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // ═══════════════════════════════════════════════════════════════════════
  // HANDLERS
  // ═══════════════════════════════════════════════════════════════════════
  
  const handleConfigChange = (key: keyof SchedulerConfig, value: any) => {
    setConfig(prev => ({ ...prev, [key]: value }));
    setSaveSuccess(false);
  };

  const handleWorkingDayToggle = (day: number) => {
    setConfig(prev => ({
      ...prev,
      workingDays: prev.workingDays.includes(day)
        ? prev.workingDays.filter(d => d !== day)
        : [...prev.workingDays, day].sort()
    }));
    setSaveSuccess(false);
  };

  const handleSaveConfig = async () => {
    setIsSaving(true);
    
    try {
      // TODO: Implementar guardado en backend
      // await saveSchedulerConfig(config);
      
      // Simular delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Guardar en localStorage por ahora
      localStorage.setItem('schedulerConfig', JSON.stringify(config));
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving config:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER SECTIONS
  // ═══════════════════════════════════════════════════════════════════════

  const renderTimezoneSection = () => (
    <Card 
      className="mb-4"
      style={{
        borderRadius: '12px',
        border: '1px solid var(--bs-border-color)',
        backgroundColor: 'var(--bs-body-bg)'
      }}
    >
      <Card.Body>
        <div className="d-flex align-items-center mb-3">
          <FontAwesomeIcon icon={faGlobe} style={{ color: '#2ca799', fontSize: '1.2rem' }} className="me-2" />
          <h6 className="mb-0">Zona Horaria</h6>
        </div>
        
        <Alert variant="info" style={{ borderRadius: '8px', fontSize: '0.9rem' }}>
          <div className="d-flex align-items-start">
            <FontAwesomeIcon icon={faInfoCircle} className="me-2 mt-1" />
            <div>
              <strong>Zona horaria actual:</strong> {currentTimezoneInfo?.label || currentTimezone}
              <br />
              <small className="text-muted">
                Los envíos se programarán en esta zona horaria por defecto
              </small>
            </div>
          </div>
        </Alert>

        <Form.Group>
          <Form.Label style={{ fontSize: '0.9rem', fontWeight: 500 }}>
            Seleccionar zona horaria
          </Form.Label>
          <Form.Select
            value={currentTimezone}
            onChange={(e) => setTimezone(e.target.value)}
            style={{
              borderRadius: '8px',
              border: '1px solid var(--bs-border-color)',
              padding: '10px'
            }}
          >
            {commonTimezones.map(tz => (
              <option key={tz.value} value={tz.value}>
                {tz.label}
              </option>
            ))}
          </Form.Select>
          <Form.Text className="text-muted">
            Puedes cambiar la zona horaria al crear cada envío individual
          </Form.Text>
        </Form.Group>
      </Card.Body>
    </Card>
  );

  const renderNotificationsSection = () => (
    <Card 
      className="mb-4"
      style={{
        borderRadius: '12px',
        border: '1px solid var(--bs-border-color)',
        backgroundColor: 'var(--bs-body-bg)'
      }}
    >
      <Card.Body>
        <div className="d-flex align-items-center mb-3">
          <FontAwesomeIcon icon={faBell} style={{ color: '#2ca799', fontSize: '1.2rem' }} className="me-2" />
          <h6 className="mb-0">Notificaciones</h6>
        </div>

        <div className="mb-3 pb-3" style={{ borderBottom: '1px solid var(--bs-border-color)' }}>
          <Form.Check
            type="switch"
            id="notify-success"
            label="Notificar cuando un envío se ejecute exitosamente"
            checked={config.notifyOnSuccess}
            onChange={(e) => handleConfigChange('notifyOnSuccess', e.target.checked)}
            style={{ fontSize: '0.9rem' }}
          />
        </div>

        <div className="mb-3 pb-3" style={{ borderBottom: '1px solid var(--bs-border-color)' }}>
          <Form.Check
            type="switch"
            id="notify-failure"
            label="Notificar cuando un envío falle"
            checked={config.notifyOnFailure}
            onChange={(e) => handleConfigChange('notifyOnFailure', e.target.checked)}
            style={{ fontSize: '0.9rem' }}
          />
        </div>

        <Row>
          <Col md={6} className="mb-3">
            <Form.Group>
              <Form.Label style={{ fontSize: '0.9rem', fontWeight: 500 }}>
                Email de notificaciones
              </Form.Label>
              <Form.Control
                type="email"
                placeholder="email@ejemplo.com"
                value={config.notificationEmail}
                onChange={(e) => handleConfigChange('notificationEmail', e.target.value)}
                disabled={!config.notifyOnSuccess && !config.notifyOnFailure}
                style={{
                  borderRadius: '8px',
                  border: '1px solid var(--bs-border-color)'
                }}
              />
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group>
              <Form.Label style={{ fontSize: '0.9rem', fontWeight: 500 }}>
                WhatsApp de notificaciones
              </Form.Label>
              <Form.Control
                type="tel"
                placeholder="+56912345678"
                value={config.notificationWhatsApp}
                onChange={(e) => handleConfigChange('notificationWhatsApp', e.target.value)}
                disabled={!config.notifyOnSuccess && !config.notifyOnFailure}
                style={{
                  borderRadius: '8px',
                  border: '1px solid var(--bs-border-color)'
                }}
              />
            </Form.Group>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );

  const renderRetriesSection = () => (
    <Card 
      className="mb-4"
      style={{
        borderRadius: '12px',
        border: '1px solid var(--bs-border-color)',
        backgroundColor: 'var(--bs-body-bg)'
      }}
    >
      <Card.Body>
        <div className="d-flex align-items-center mb-3">
          <FontAwesomeIcon icon={faRepeat} style={{ color: '#2ca799', fontSize: '1.2rem' }} className="me-2" />
          <h6 className="mb-0">Reintentos Automáticos</h6>
        </div>

        <div className="mb-3 pb-3" style={{ borderBottom: '1px solid var(--bs-border-color)' }}>
          <Form.Check
            type="switch"
            id="enable-retries"
            label="Habilitar reintentos automáticos cuando un envío falle"
            checked={config.enableRetries}
            onChange={(e) => handleConfigChange('enableRetries', e.target.checked)}
            style={{ fontSize: '0.9rem' }}
          />
        </div>

        <Row>
          <Col md={6} className="mb-3">
            <Form.Group>
              <Form.Label style={{ fontSize: '0.9rem', fontWeight: 500 }}>
                Número máximo de reintentos
              </Form.Label>
              <Form.Control
                type="number"
                min={1}
                max={10}
                value={config.maxRetries}
                onChange={(e) => handleConfigChange('maxRetries', parseInt(e.target.value))}
                disabled={!config.enableRetries}
                style={{
                  borderRadius: '8px',
                  border: '1px solid var(--bs-border-color)'
                }}
              />
              <Form.Text className="text-muted">
                Entre 1 y 10 reintentos
              </Form.Text>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group>
              <Form.Label style={{ fontSize: '0.9rem', fontWeight: 500 }}>
                Delay entre reintentos (minutos)
              </Form.Label>
              <Form.Control
                type="number"
                min={1}
                max={60}
                value={config.retryDelayMinutes}
                onChange={(e) => handleConfigChange('retryDelayMinutes', parseInt(e.target.value))}
                disabled={!config.enableRetries}
                style={{
                  borderRadius: '8px',
                  border: '1px solid var(--bs-border-color)'
                }}
              />
              <Form.Text className="text-muted">
                Entre 1 y 60 minutos
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );

  const renderWorkingHoursSection = () => {
    const daysOfWeek = [
      { value: 0, label: 'Dom' },
      { value: 1, label: 'Lun' },
      { value: 2, label: 'Mar' },
      { value: 3, label: 'Mié' },
      { value: 4, label: 'Jue' },
      { value: 5, label: 'Vie' },
      { value: 6, label: 'Sáb' }
    ];

    return (
      <Card 
        className="mb-4"
        style={{
          borderRadius: '12px',
          border: '1px solid var(--bs-border-color)',
          backgroundColor: 'var(--bs-body-bg)'
        }}
      >
        <Card.Body>
          <div className="d-flex align-items-center mb-3">
            <FontAwesomeIcon icon={faClock} style={{ color: '#2ca799', fontSize: '1.2rem' }} className="me-2" />
            <h6 className="mb-0">Horario de Trabajo</h6>
          </div>

          <div className="mb-3 pb-3" style={{ borderBottom: '1px solid var(--bs-border-color)' }}>
            <Form.Check
              type="switch"
              id="enable-working-hours"
              label="Restringir envíos a horario de trabajo"
              checked={config.enableWorkingHours}
              onChange={(e) => handleConfigChange('enableWorkingHours', e.target.checked)}
              style={{ fontSize: '0.9rem' }}
            />
            <Form.Text className="text-muted d-block mt-1">
              Los envíos programados fuera de este horario se ejecutarán en la próxima ventana disponible
            </Form.Text>
          </div>

          <Row className="mb-3">
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label style={{ fontSize: '0.9rem', fontWeight: 500 }}>
                  Hora de inicio
                </Form.Label>
                <Form.Control
                  type="time"
                  value={config.workingHoursStart}
                  onChange={(e) => handleConfigChange('workingHoursStart', e.target.value)}
                  disabled={!config.enableWorkingHours}
                  style={{
                    borderRadius: '8px',
                    border: '1px solid var(--bs-border-color)'
                  }}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label style={{ fontSize: '0.9rem', fontWeight: 500 }}>
                  Hora de término
                </Form.Label>
                <Form.Control
                  type="time"
                  value={config.workingHoursEnd}
                  onChange={(e) => handleConfigChange('workingHoursEnd', e.target.value)}
                  disabled={!config.enableWorkingHours}
                  style={{
                    borderRadius: '8px',
                    border: '1px solid var(--bs-border-color)'
                  }}
                />
              </Form.Group>
            </Col>
          </Row>

          <div>
            <Form.Label style={{ fontSize: '0.9rem', fontWeight: 500, marginBottom: '10px' }}>
              Días de trabajo
            </Form.Label>
            <div className="d-flex gap-2 flex-wrap">
              {daysOfWeek.map(day => (
                <Badge
                  key={day.value}
                  bg={config.workingDays.includes(day.value) ? 'primary' : 'secondary'}
                  style={{
                    cursor: config.enableWorkingHours ? 'pointer' : 'not-allowed',
                    opacity: config.enableWorkingHours ? 1 : 0.5,
                    padding: '8px 16px',
                    fontSize: '0.85rem',
                    fontWeight: 500,
                    backgroundColor: config.workingDays.includes(day.value) ? '#2ca799' : 'var(--bs-secondary-bg)',
                    color: config.workingDays.includes(day.value) ? 'white' : 'var(--bs-secondary-color)',
                    transition: 'all 0.2s ease'
                  }}
                  onClick={() => config.enableWorkingHours && handleWorkingDayToggle(day.value)}
                >
                  {day.label}
                </Badge>
              ))}
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  };

  const renderSystemInfoSection = () => (
    <Card 
      style={{
        borderRadius: '12px',
        border: '1px solid var(--bs-border-color)',
        backgroundColor: 'var(--bs-body-bg)'
      }}
    >
      <Card.Body>
        <div className="d-flex align-items-center mb-3">
          <FontAwesomeIcon icon={faInfoCircle} style={{ color: '#2ca799', fontSize: '1.2rem' }} className="me-2" />
          <h6 className="mb-0">Información del Sistema</h6>
        </div>
        
        <Row>
          <Col md={4} className="mb-3">
            <div style={{ 
              padding: '16px', 
              borderRadius: '8px', 
              backgroundColor: 'var(--bs-secondary-bg)',
              border: '1px solid var(--bs-border-color)'
            }}>
              <div style={{ fontSize: '0.85rem', color: 'var(--bs-secondary-color)', marginBottom: '4px' }}>
                Total Programados
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#2ca799' }}>
                {schedules.length}
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div style={{ 
              padding: '16px', 
              borderRadius: '8px', 
              backgroundColor: 'var(--bs-secondary-bg)',
              border: '1px solid var(--bs-border-color)'
            }}>
              <div style={{ fontSize: '0.85rem', color: 'var(--bs-secondary-color)', marginBottom: '4px' }}>
                Campañas Activas
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#3b82f6' }}>
                {activeCampaigns.length}
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div style={{ 
              padding: '16px', 
              borderRadius: '8px', 
              backgroundColor: 'var(--bs-secondary-bg)',
              border: '1px solid var(--bs-border-color)'
            }}>
              <div style={{ fontSize: '0.85rem', color: 'var(--bs-secondary-color)', marginBottom: '4px' }}>
                Próximos Hoy
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#f59e0b' }}>
                {stats?.upcomingToday || 0}
              </div>
            </div>
          </Col>
        </Row>

        <Alert variant="info" className="mb-0" style={{ borderRadius: '8px', fontSize: '0.85rem' }}>
          <strong>Versión del Scheduler:</strong> v2.5.0
          <br />
          <strong>Último chequeo:</strong> {new Date().toLocaleString('es-CL')}
        </Alert>
      </Card.Body>
    </Card>
  );

  // ═══════════════════════════════════════════════════════════════════════
  // MAIN RENDER
  // ═══════════════════════════════════════════════════════════════════════
  
  return (
    <div className="py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h5 className="mb-1">
            <FontAwesomeIcon icon={faCog} className="me-2" style={{ color: '#2ca799' }} />
            Configuración del Scheduler
          </h5>
          <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
            Personaliza el comportamiento del sistema de programación
          </p>
        </div>

        <Button
          variant="primary"
          size="sm"
          onClick={handleSaveConfig}
          disabled={isSaving}
          style={{
            backgroundColor: '#2ca799',
            border: 'none',
            borderRadius: '8px',
            padding: '8px 20px'
          }}
        >
          {isSaving ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" />
              Guardando...
            </>
          ) : saveSuccess ? (
            <>
              <FontAwesomeIcon icon={faCheck} className="me-2" />
              Guardado
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faSave} className="me-2" />
              Guardar Cambios
            </>
          )}
        </Button>
      </div>

      {/* Success Alert */}
      {saveSuccess && (
        <Alert variant="success" dismissible onClose={() => setSaveSuccess(false)} style={{ borderRadius: '8px' }}>
          <FontAwesomeIcon icon={faCheck} className="me-2" />
          Configuración guardada exitosamente
        </Alert>
      )}

      {/* Sections */}
      {renderTimezoneSection()}
      {renderNotificationsSection()}
      {renderRetriesSection()}
      {renderWorkingHoursSection()}
      {renderSystemInfoSection()}
    </div>
  );
};

export default SchedulerConfigurationTab;